import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import { apiService } from '@/services/api'
import { useToast } from 'vue-toastification'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const business = ref(null)
  const token = ref(localStorage.getItem('pos_token') || null)
  const isLoading = ref(false)
  
  // Toast instance
  const toast = useToast()
  
  // Computed
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const businessId = computed(() => {
    // Try different possible business ID fields
    const businessIdFromBusiness = business.value?._id || business.value?.id
    let businessIdFromUser = user.value?.business_id
    
    // If business_id from user is an object, extract the ID
    if (typeof businessIdFromUser === 'object' && businessIdFromUser !== null) {
      businessIdFromUser = businessIdFromUser._id || businessIdFromUser.id
    }
    

    
    return businessIdFromBusiness || businessIdFromUser
  })
  const businessName = computed(() => business.value?.name)
  const userRole = computed(() => user.value?.role)
  
  // Actions
  const login = async (credentials) => {
    isLoading.value = true
    try {
      const response = await apiService.post('/auth/login', credentials)
      
      // console.log('Login response received:', {
      //   hasToken: !!response.token,
      //   hasUser: !!response.user,
      //   userBusinessId: response.user?.business_id,
      //   userBusiness: response.user?.business,
      //   fullResponse: response
      // })
      
      if (response.token && response.user) {
        // Store authentication data
        token.value = response.token
        user.value = response.user
        business.value = response.user.business || null
        
        // console.log('Setting auth state:', {
        //   token: !!token.value,
        //   userId: user.value?.id,
        //   userBusinessId: user.value?.business_id,
        //   businessFromUser: user.value?.business,
        //   businessStored: business.value
        // })
        
        // Store token in localStorage
        localStorage.setItem('pos_token', response.token)
        localStorage.setItem('pos_user', JSON.stringify(response.user))
        if (response.user.business) {
          localStorage.setItem('pos_business', JSON.stringify(response.user.business))
          // console.log('Business stored in localStorage:', response.user.business)
        } else {
          // console.log('No business in user response to store')
        }
        
        // Update API service token
        apiService.setAuthToken(response.token)
        
        toast.success(`Welcome back, ${response.user.username}!`)
        
        // Check subscription status for non-superadmin users
        if (response.user.role !== 'superadmin') {
          // Import subscription store and monitor
          const { useSubscriptionStore } = await import('./subscription')
          const { subscriptionMonitor } = await import('@/services/subscriptionMonitor')
          const subscriptionStore = useSubscriptionStore()
          
          // Check subscription (this will set the subscription status)
          try {
            await subscriptionStore.checkSubscription()
          } catch (error) {
            console.warn('Initial subscription check failed:', error)
          }

          // Start subscription monitoring
          subscriptionMonitor.start()
        }
        
        return { success: true }
      } else {
        throw new Error(response.message || 'Login failed')
      }
    } catch (error) {
      console.error('Login error:', error)
      const message = error.response?.data?.message || error.message || 'Login failed'
      toast.error(message)
      return { success: false, message }
    } finally {
      isLoading.value = false
    }
  }
  
  const logout = async () => {
    // Stop subscription monitoring
    try {
      const { subscriptionMonitor } = await import('@/services/subscriptionMonitor')
      subscriptionMonitor.stop()
    } catch (error) {
      console.warn('Failed to stop subscription monitoring:', error)
    }

    // Clear state
    user.value = null
    business.value = null
    token.value = null
    
    // Clear localStorage
    localStorage.removeItem('pos_token')
    localStorage.removeItem('pos_user')
    localStorage.removeItem('pos_business')
    
    // Clear API service token and subscription cache
    apiService.clearAuthToken()
    apiService.clearSubscriptionCache()
    
    toast.info('You have been logged out')
  }
  
  const initializeAuth = () => {
    try {
      // Restore from localStorage
      const savedToken = localStorage.getItem('pos_token')
      const savedUser = localStorage.getItem('pos_user')
      const savedBusiness = localStorage.getItem('pos_business')
      
      if (savedToken && savedUser) {
        token.value = savedToken
        user.value = JSON.parse(savedUser)
        business.value = savedBusiness ? JSON.parse(savedBusiness) : null
        
        // If we don't have business object but user has business_id, create a minimal business object
        if (!business.value && user.value?.business_id) {
          business.value = {
            _id: user.value.business_id,
            id: user.value.business_id
          }

        }
        
        // Set API token
        apiService.setAuthToken(savedToken)
        
        console.log('Auth restored from localStorage:', {
          userId: user.value?.id,
          userBusinessId: user.value?.business_id,
          businessId: business.value?._id || business.value?.id
        })
      }
    } catch (error) {
      console.error('Failed to restore auth:', error)
      logout() // Clear corrupted data
    }
  }
  
  const loadBusinessContext = async () => {
    if (!businessId.value) {
      throw new Error('No business ID available')
    }
    
    try {
      const response = await apiService.get(`/businesses/${businessId.value}`)
      

      
      let businessData = null
      
      // Handle response format { business } or { success: true, business }
      if (response.business) {
        businessData = response.business
      } else if (response.success && response.business) {
        businessData = response.business
      } else {
        throw new Error(response.message || 'Failed to load business context')
      }
      
      // Ensure whatsapp_invoice_sharing field is always present with default value
      if (businessData.whatsapp_invoice_sharing === undefined || businessData.whatsapp_invoice_sharing === null) {
        businessData.whatsapp_invoice_sharing = false
      }
      
      business.value = businessData
      localStorage.setItem('pos_business', JSON.stringify(businessData))
      
      // Check for business suspension status and notify subscription store
      if (businessData.is_suspended) {
        console.log('Business is suspended, notifying subscription store:', {
          suspension_status: businessData.suspension_status,
          suspension_reason: businessData.suspension_reason,
          suspended_at: businessData.suspended_at
        })
        // Emit suspension event
        window.dispatchEvent(new CustomEvent('subscription-suspended', {
          detail: {
            suspension_status: businessData.suspension_status,
            suspension_reason: businessData.suspension_reason,
            suspended_at: businessData.suspended_at,
            suspended_by: businessData.suspended_by
          }
        }))
      } else {
        // Business is not suspended - clear any cached suspension data

        window.dispatchEvent(new CustomEvent('subscription-reactivated'))
      }
      
      return businessData
    } catch (error) {
      console.error('Failed to load business context:', error)
      throw error
    }
  }
  
  const updateProfile = async (profileData) => {
    isLoading.value = true
    try {
      const response = await apiService.put(`/users/${user.value._id}`, profileData)
      
      if (response.success) {
        user.value = { ...user.value, ...response.user }
        localStorage.setItem('pos_user', JSON.stringify(user.value))
        toast.success('Profile updated successfully')
        return { success: true }
      } else {
        throw new Error(response.message || 'Failed to update profile')
      }
    } catch (error) {
      console.error('Update profile error:', error)
      const message = error.response?.data?.message || error.message || 'Failed to update profile'
      toast.error(message)
      return { success: false, message }
    } finally {
      isLoading.value = false
    }
  }
  
  const checkTokenValidity = async () => {
    if (!token.value) return false
    
    try {
      const response = await apiService.get('/auth/verify')
      return response.success
    } catch (error) {
      console.error('Token validation failed:', error)
      logout()
      return false
    }
  }
  
  // Return store interface
  return {
    // State
    user: readonly(user),
    business: readonly(business),
    token: readonly(token),
    isLoading: readonly(isLoading),
    
    // Computed
    isAuthenticated,
    businessId,
    businessName,
    userRole,
    
    // Actions
    login,
    logout,
    initializeAuth,
    loadBusinessContext,
    updateProfile,
    checkTokenValidity
  }
})
